/**
 * MILESTONE M2 MINIMAL NEUTRAL DERIVED PROGRESS ENGINE
 *
 * In M2 (Local Data + Account + Sync + Walking Skeleton), progress calculation
 * is strictly minimal, neutral, and bounded:
 *   - attemptCount
 *   - completedActivityCount
 *   - lastAttemptScore
 *   - averageAttemptScore
 *   - basic activity/skill completion
 *
 * INVARIANT: This minimal M2 progress calculation is NOT an Agent Readiness Score,
 * does NOT compute mathematical Evidence Confidence calculus or exponential freshness decay,
 * and does NOT execute dynamic profile matrix weighting.
 * All advanced Training Intelligence and ARS algorithms belong strictly to Milestone M3
 * (Training Intelligence + Generic Foundation Pack) as specified in docs/SCORING_SPEC.md.
 */

import type {
  CompetencyMastery,
  LearnerProfile,
  Pillar,
  TrainingAttempt,
} from '../models';

export interface MinimalSkillProgress {
  skillId: string;
  competencyName: string;
  pillar: Pillar;
  attemptCount: number;
  lastAttemptScore: number | null;
  averageAttemptScore: number | null;
  lastAttemptAt: string | null;
  completed: boolean;
}

export function recomputeCompetencyProgress(
  skillId: string,
  competencyName: string,
  pillar: Pillar,
  attempts: TrainingAttempt[]
): CompetencyMastery {
  if (attempts.length === 0) {
    return {
      skillId,
      competencyName,
      pillar,
      score: null,
      evidenceConfidence: 0,
      evidenceFreshness: 'fresh',
      lastAttemptAt: null,
      masteryState: 'not_started',
      totalAttempts: 0,
    };
  }

  const sorted = [...attempts].sort(
    (a, b) => new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime()
  );

  const lastAttempt = sorted[sorted.length - 1];
  const lastScore = lastAttempt.score;
  const avgScore =
    Math.round(
      (sorted.reduce((acc, a) => acc + a.score, 0) / sorted.length) * 10
    ) / 10;

  // Discrete M2 basic completion indicator
  const completed = lastScore >= 70;
  const masteryState: CompetencyMastery['masteryState'] = completed
    ? 'practicing'
    : 'learning';

  return {
    skillId,
    competencyName,
    pillar,
    score: avgScore, // Neutral average score
    evidenceConfidence: 0, // Dormant in M2; active calculus in M3
    evidenceFreshness: 'fresh', // Dormant in M2; decay calculus in M3
    lastAttemptAt: lastAttempt.completedAt,
    masteryState,
    totalAttempts: sorted.length,
  };
}

export function recomputeLearnerProgress(
  userId: string,
  competencies: CompetencyMastery[],
  displayName = 'Agent 402',
  jobProfile = 'inbound_telecom_care'
): LearnerProfile {
  const pillarScores: Record<Pillar, { sum: number; count: number }> = {
    communication: { sum: 0, count: 0 },
    support: { sum: 0, count: 0 },
    sales: { sum: 0, count: 0 },
  };

  for (const comp of competencies) {
    if (comp.score !== null) {
      pillarScores[comp.pillar].sum += comp.score;
      pillarScores[comp.pillar].count += 1;
    }
  }

  const commScore =
    pillarScores.communication.count > 0
      ? Math.round(
          (pillarScores.communication.sum / pillarScores.communication.count) * 10
        ) / 10
      : null;

  const suppScore =
    pillarScores.support.count > 0
      ? Math.round(
          (pillarScores.support.sum / pillarScores.support.count) * 10
        ) / 10
      : null;

  const saleScore =
    pillarScores.sales.count > 0
      ? Math.round(
          (pillarScores.sales.sum / pillarScores.sales.count) * 10
        ) / 10
      : null;

  return {
    userId,
    displayName,
    activeJobProfile: jobProfile,
    // INVARIANT: agentReadinessScore is strictly null in M2; ARS belongs to M3
    agentReadinessScore: null,
    communicationScore: commScore,
    supportScore: suppScore,
    salesScore: saleScore,
    lastCalculatedAt: new Date().toISOString(),
  };
}
